import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { TOAST } from '../utils/toastMessages';
import { 
  Gift, 
  Link2, 
  Copy, 
  Check, 
  Clock, 
  CheckCircle2, 
  Award,
  Users,
  Loader,
  RefreshCw,
  Share2,
  Info
} from 'lucide-react';
import { openShareUrl } from '../utils/navigation';

const API = process.env.REACT_APP_BACKEND_URL;

const ReferralRewardsCard = ({ user, profileStrength }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/users/me/referral-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const copyLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      setLinkCopied(true);
      toast.success(TOAST.REFERRAL_LINK_COPIED);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const shareLink = async (platform) => {
    if (!stats?.referral_link) return;
    
    const url = encodeURIComponent(stats.referral_link);
    const rawUrl = stats.referral_link;
    const text = encodeURIComponent('Join me on CeleyApp - the service provider marketplace!');
    
    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${text}%20${url}`;
        break;
      default:
        return;
    }
    await openShareUrl(shareUrl);
  };

  if (loading) {
    return (
      <Card data-testid="referral-rewards-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Gift className="w-5 h-5 text-emerald-600" />
            Referral Rewards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  const isVendor = stats.is_vendor;
  const hasReferrals = stats.total_referrals > 0;

  return (
    <Card data-testid="referral-rewards-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gift className="w-5 h-5 text-emerald-600" />
              Referral Rewards
            </CardTitle>
            <CardDescription>
              Invite friends and earn platform fee waivers
            </CardDescription>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={fetchStats}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Fee Waiver Status (for vendors) — contextual to rewards; softened style */}
        {isVendor && (
          <div className="bg-emerald-50/60 rounded-lg p-3.5 border border-emerald-100/80">
            {stats.fee_waiver_active ? (
              <div className="flex items-start gap-2.5">
                <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                  <Award className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-emerald-800">Fee Waiver Active</div>
                  <div className="text-xs text-emerald-700/80">
                    No platform fees until {new Date(stats.fee_waiver_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  {stats.total_months_granted > 0 && (
                    <div className="text-[11px] text-emerald-600/70 mt-0.5">
                      {stats.total_months_granted} month(s) total granted
                    </div>
                  )}
                </div>
              </div>
            ) : stats.fee_waiver_expired ? (
              <div className="flex items-start gap-2.5">
                <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <Award className="w-4 h-4 text-gray-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-gray-700">No Active Fee Waiver</div>
                  <div className="text-xs text-gray-500">
                    Refer providers to earn fee-free time
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5">
                <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <Info className="w-4 h-4 text-gray-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-gray-700">No Active Fee Waiver</div>
                  <div className="text-xs text-gray-500">
                    Refer providers to earn fee-free time
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unlock Higher Rewards — Profile Strength Tie-In */}
        {isVendor && profileStrength && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100" data-testid="unlock-rewards-section">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-indigo-100 rounded-full shrink-0">
                <Award className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-indigo-800 mb-1">Unlock Higher Rewards</div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 bg-white/60 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        profileStrength.score >= 90 ? 'bg-emerald-500' : profileStrength.score >= 60 ? 'bg-indigo-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${profileStrength.score}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-indigo-700 shrink-0">{profileStrength.score}%</span>
                </div>
                {profileStrength.score >= 90 ? (
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span className="text-sm text-emerald-700 font-medium">Rewards unlocked</span>
                  </div>
                ) : profileStrength.score >= 60 ? (
                  <p className="text-xs text-indigo-600">Almost there! Complete your profile to unlock enhanced referral rewards and visibility.</p>
                ) : (
                  <p className="text-xs text-indigo-600">Complete your profile to unlock referral rewards. Fully prepared providers earn enhanced rewards and visibility.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Non-vendor notice */}
        {!isVendor && (
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <div className="font-medium text-blue-800">Rewards Apply to Providers</div>
                <div className="text-sm text-blue-600">
                  Referral rewards (platform fee waivers) apply once you become a service provider. 
                  You can still share your link and track referrals!
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Referral Link */}
        {stats.referral_link && (
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <Link2 className="w-4 h-4" />
              Your Referral Link
            </label>
            {/* Link display - stacked on mobile for better readability */}
            <div className="flex flex-col gap-2">
              <input
                type="text"
                readOnly
                value={stats.referral_link}
                className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md font-mono truncate"
                data-testid="referral-link-input"
                onClick={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={copyLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                data-testid="copy-referral-link-btn"
              >
                {linkCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                {linkCopied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
            {/* Share buttons - grid layout for mobile */}
            <div className="space-y-2">
              <span className="text-xs text-gray-500">Share:</span>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-2 gap-1 text-xs justify-center"
                  onClick={() => shareLink('facebook')}
                >
                  <svg className="w-4 h-4 text-blue-600 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                  </svg>
                  <span className="truncate">Facebook</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-2 gap-1 text-xs justify-center"
                  onClick={() => shareLink('twitter')}
                >
                  <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  <span className="truncate">X</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-2 gap-1 text-xs justify-center"
                  onClick={() => shareLink('whatsapp')}
                >
                  <svg className="w-4 h-4 text-green-600 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="truncate">WhatsApp</span>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Referral Stats */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Your Referrals</span>
            <Badge variant="secondary" className="text-xs">
              {stats.total_referrals} total
            </Badge>
          </div>
          
          {hasReferrals ? (
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-100">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Clock className="w-3.5 h-3.5 text-yellow-600" />
                  <span className="text-lg font-bold text-yellow-700">{stats.pending_referrals}</span>
                </div>
                <div className="text-xs text-yellow-600">Pending</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-100">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Gift className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-lg font-bold text-emerald-700">{stats.eligible_referrals}</span>
                </div>
                <div className="text-xs text-emerald-600">Eligible</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Award className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-lg font-bold text-blue-700">{stats.rewarded_referrals}</span>
                </div>
                <div className="text-xs text-blue-600">Rewarded</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Share2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No referrals yet</p>
              <p className="text-xs text-gray-400">Share your link to start earning rewards!</p>
            </div>
          )}
        </div>

        {/* Status Explanation */}
        <div className="border-t pt-3">
          <div className="text-xs text-gray-500 space-y-1">
            <div className="flex items-center gap-2">
              <Badge className="bg-yellow-100 text-yellow-700 text-xs py-0">Pending</Badge>
              <span>Waiting for referral to complete provider profile</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700 text-xs py-0">Eligible</Badge>
              <span>Provider with complete profile - ready for reward</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700 text-xs py-0">Rewarded</Badge>
              <span>You've received a fee waiver for this referral</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralRewardsCard;
